import { __rest } from "tslib";
import { registerPlugin } from '@capacitor/core';
// Eagerly finish OAuth popup redirects before plugin lazy-load (COOP-safe)
import './oauth-popup-redirect';
import { asLinkedInLoginResult, buildLinkedInLoginOptions, buildLinkedInOAuthConfig, LINKEDIN_PROVIDER_ID, markLinkedInRedirectPending, } from './linkedin-provider';
import { asTikTokLoginResult, buildTikTokLoginOptions, buildTikTokOAuthConfig, isTikTokOAuthResult, TIKTOK_PROVIDER_ID, } from './tiktok-provider';
const GOOGLE_OFFLINE_REFRESH_MESSAGE = "Google refresh() is not available when using offline mode. Offline mode only returns serverAuthCode for backend token exchange. Send serverAuthCode to your backend and refresh tokens there, or switch google.mode to 'online' for client-side refresh.";
const rawSocialLogin = registerPlugin('SocialLogin', {
    web: () => import('./web').then((m) => new m.SocialLoginWeb()),
});
class SocialLoginClient {
    normalizeInitializeOptions(options) {
        var _a, _b;
        let next = options;
        if (next.linkedin) {
            const { linkedin, oauth2 } = next, rest = __rest(next, ["linkedin", "oauth2"]);
            next = Object.assign(Object.assign({}, rest), { linkedin, oauth2: Object.assign(Object.assign({}, (oauth2 !== null && oauth2 !== void 0 ? oauth2 : {})), { [LINKEDIN_PROVIDER_ID]: (_a = oauth2 === null || oauth2 === void 0 ? void 0 : oauth2[LINKEDIN_PROVIDER_ID]) !== null && _a !== void 0 ? _a : buildLinkedInOAuthConfig(linkedin) }) });
        }
        if (next.tiktok) {
            if (!next.tiktok.clientKey || !next.tiktok.redirectUrl) {
                throw new Error('tiktok.clientKey and tiktok.redirectUrl are required');
            }
            const { tiktok, oauth2 } = next, rest = __rest(next, ["tiktok", "oauth2"]);
            next = Object.assign(Object.assign({}, rest), { tiktok, oauth2: Object.assign(Object.assign({}, (oauth2 !== null && oauth2 !== void 0 ? oauth2 : {})), { [TIKTOK_PROVIDER_ID]: (_b = oauth2 === null || oauth2 === void 0 ? void 0 : oauth2[TIKTOK_PROVIDER_ID]) !== null && _b !== void 0 ? _b : buildTikTokOAuthConfig(tiktok) }) });
        }
        return next;
    }
    constructor() {
        this.initialize = this.initialize.bind(this);
        this.refresh = this.refresh.bind(this);
    }
    async initialize(options) {
        const normalized = this.normalizeInitializeOptions(options);
        await rawSocialLogin.initialize(normalized);
        this.initializeOptions = normalized;
    }
    async login(options) {
        var _a;
        if (options.provider === 'linkedin') {
            const linkedInOptions = (_a = options.options) !== null && _a !== void 0 ? _a : {};
            const oauthOptions = buildLinkedInLoginOptions(linkedInOptions);
            if (oauthOptions.flow === 'redirect' && oauthOptions.state) {
                markLinkedInRedirectPending(oauthOptions.state);
            }
            const response = await rawSocialLogin.login({
                provider: 'oauth2',
                options: oauthOptions,
            });
            return asLinkedInLoginResult(response);
        }
        if (options.provider === 'tiktok') {
            const response = await rawSocialLogin.login({
                provider: 'oauth2',
                options: buildTikTokLoginOptions(options.options),
            });
            return asTikTokLoginResult(response);
        }
        return rawSocialLogin.login(options);
    }
    async logout(options) {
        if (options.provider === 'linkedin') {
            return rawSocialLogin.logout({ provider: 'oauth2', providerId: LINKEDIN_PROVIDER_ID });
        }
        if (options.provider === 'tiktok') {
            return rawSocialLogin.logout({ provider: 'oauth2', providerId: TIKTOK_PROVIDER_ID });
        }
        return rawSocialLogin.logout(options);
    }
    async isLoggedIn(options) {
        if (options.provider === 'linkedin') {
            return rawSocialLogin.isLoggedIn({ provider: 'oauth2', providerId: LINKEDIN_PROVIDER_ID });
        }
        if (options.provider === 'tiktok') {
            return rawSocialLogin.isLoggedIn({ provider: 'oauth2', providerId: TIKTOK_PROVIDER_ID });
        }
        return rawSocialLogin.isLoggedIn(options);
    }
    async getAuthorizationCode(options) {
        if (options.provider === 'linkedin') {
            return rawSocialLogin.getAuthorizationCode({ provider: 'oauth2', providerId: LINKEDIN_PROVIDER_ID });
        }
        if (options.provider === 'tiktok') {
            return rawSocialLogin.getAuthorizationCode({ provider: 'oauth2', providerId: TIKTOK_PROVIDER_ID });
        }
        return rawSocialLogin.getAuthorizationCode(options);
    }
    async refresh(options) {
        var _a, _b, _c;
        if (options.provider === 'google' && ((_b = (_a = this.initializeOptions) === null || _a === void 0 ? void 0 : _a.google) === null || _b === void 0 ? void 0 : _b.mode) === 'offline') {
            console.warn(`[SocialLogin] ${GOOGLE_OFFLINE_REFRESH_MESSAGE}`);
        }
        if (options.provider === 'linkedin') {
            return rawSocialLogin.refresh({
                provider: 'oauth2',
                options: buildLinkedInLoginOptions((_c = options.options) !== null && _c !== void 0 ? _c : {}),
            });
        }
        if (options.provider === 'tiktok') {
            return rawSocialLogin.refresh({
                provider: 'oauth2',
                options: buildTikTokLoginOptions(options.options),
            });
        }
        return rawSocialLogin.refresh(options);
    }
    async refreshToken(options) {
        if (options.provider === 'linkedin') {
            return rawSocialLogin.refreshToken({
                provider: 'oauth2',
                providerId: LINKEDIN_PROVIDER_ID,
                refreshToken: options.refreshToken,
                additionalParameters: options.additionalParameters,
            });
        }
        if (options.provider === 'tiktok') {
            return rawSocialLogin.refreshToken({
                provider: 'oauth2',
                providerId: TIKTOK_PROVIDER_ID,
                refreshToken: options.refreshToken,
                additionalParameters: options.additionalParameters,
            });
        }
        return rawSocialLogin.refreshToken(options);
    }
    async handleRedirectCallback() {
        const result = await rawSocialLogin.handleRedirectCallback();
        if (result && isTikTokOAuthResult(result)) {
            return asTikTokLoginResult(result);
        }
        return result;
    }
    async decodeIdToken(options) {
        return rawSocialLogin.decodeIdToken(options);
    }
    async getAccessTokenExpirationDate(options) {
        return rawSocialLogin.getAccessTokenExpirationDate(options);
    }
    async isAccessTokenAvailable(options) {
        return rawSocialLogin.isAccessTokenAvailable(options);
    }
    async isAccessTokenExpired(options) {
        return rawSocialLogin.isAccessTokenExpired(options);
    }
    async isRefreshTokenAvailable(options) {
        return rawSocialLogin.isRefreshTokenAvailable(options);
    }
    async providerSpecificCall(options) {
        return rawSocialLogin.providerSpecificCall(options);
    }
    async getPluginVersion() {
        return rawSocialLogin.getPluginVersion();
    }
    async openSecureWindow(options) {
        return rawSocialLogin.openSecureWindow(options);
    }
    async openAuthSession(options) {
        return rawSocialLogin.openAuthSession(options);
    }
}
export const SocialLoginBase = rawSocialLogin;
export const SocialLogin = new SocialLoginClient();
//# sourceMappingURL=social-login.js.map